import { inject, injectable } from 'tsyringe';

import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { AsyncLock } from '../../../utils/async-lock';
import { LoggerService } from '../../logger.service';
import { DelegateProvider } from '../../../interfaces/delegate-provider.interface';
import { IOwGamesWrapper } from '../../../interfaces/ow-wrappers/ow-games-wrapper.interface';
import { IOWGameListenerWrapper } from '../../../interfaces/ow-wrappers/ow-game-listener-wrapper.interface';
import {
  kDITOWGamesWrapper,
  kDITOWGameListenerWrapper,
} from '../../../constants/injectionTokens';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.Global)
export class GlobalConditionGameRunning
  implements ICondition, IOWGameListenerWrapper
{
  private readonly logger = LoggerService.getCategory(
    'GlobalConditionGameRunning',
  );

  private _waitingForGameId?: number;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    @inject(kDITOWGamesWrapper)
    private readonly owGamesWrapper: IOwGamesWrapper,
    private readonly singleRequestLock: AsyncLock,
    @inject(kDITOWGameListenerWrapper)
    private readonly owGameListener: DelegateProvider<IOWGameListenerWrapper>,
  ) {
    this.owGameListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { game } = context;
    const configs = await this.datastoreService.getGlobalConfigurations();
    if (!configs) {
      this.logger.warn('Failed to get server configurations - returning false');
      return [false, NoRecommendationsReasons.FailedToGetServiceConfig];
    }

    const gameConfig = await this.datastoreService.getGame(game.id);
    const isGameRunning: boolean = await this.isGameRunning(
      game.id,
      gameConfig.shouldShowOnStart,
      gameConfig.targetGameId,
    );

    if (isGameRunning) {
      return [false, NoRecommendationsReasons.GameIsRunning];
    }

    return [true, undefined];
  }

  // ---------------------------------------------------------------------------
  async shouldSkip(context: ConditionContext): Promise<boolean> {
    // We will skip The game running condition in case the game has no
    // targetGameId and it is configured to show on start,
    // Thus there is no need to apply the game running condition
    const { game } = context;
    const configs = await this.datastoreService.getGlobalConfigurations();
    if (!configs) {
      this.logger.warn('Failed to get server configurations - returning false');
      return false;
    }

    const gameConfig = await this.datastoreService.getGame(game.id);
    if (game.targetGameId > 0) {
      return false;
    }

    return gameConfig?.shouldShowOnStart ?? false;
  }

  // ---------------------------------------------------------------------------
  private async isGameRunning(
    gameId: number,
    shouldShowOnStart: boolean,
    targetGameId: number,
  ) {
    // todo - in ow there is one, in electron there could be many,
    // CRN should use the most recent once.
    const runningGameInfo = await this.owGamesWrapper.getRunningGameInfo();

    if (!runningGameInfo) {
      this.logger.info('runningGameInfo', runningGameInfo);
      return false;
    }

    const msg = `A game [${runningGameInfo.classId}] is currently running`;
    this.logger.info(msg);

    if (!shouldShowOnStart) {
      return true;
    }

    if (targetGameId === runningGameInfo.classId) {
      const msg =
        'Target game is currently running, shouldShowOnStart = true' +
        'waiting for game to close, and launcher to load';
      this.logger.info(msg);

      await this.waitForGameToEnd(runningGameInfo.classId);
      return false;
    }

    this.logger.info('shouldShowOnStart:', !shouldShowOnStart);
    return !shouldShowOnStart;
  }

  // ---------------------------------------------------------------------------
  private async waitForGameToEnd(gameId: number) {
    this._waitingForGameId = gameId;

    // acquire first lock
    await this.singleRequestLock.lock();

    // acquire the lock again - which should be blocked until the unlock will be
    // called
    await this.singleRequestLock.lock();
  }

  // ---------------------------------------------------------------------------
  // OWGameListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onGameTerminated(info: any) {
    const candidate = info?.gameInfo?.classId ?? info?.classId ?? info;
    const terminatedGameId =
      typeof candidate === 'number' ? candidate : undefined;

    if (
      terminatedGameId === undefined ||
      this._waitingForGameId !== terminatedGameId
    ) {
      this.logger.info(
        `Game ${terminatedGameId} terminated, but waiting for ` +
          `${this._waitingForGameId} - ignoring`,
      );
      return;
    }

    this.logger.info('Target game terminated, unlocking');
    this._waitingForGameId = undefined;
    await this.singleRequestLock.unlock();
  }
}
